import { createElement, Fragment, type ReactNode } from "react";
import parse, {
  domToReact,
  type DOMNode,
  type HTMLReactParserOptions,
} from "html-react-parser";
import type {
  AssessmentQuestion,
  ReadingAssessmentContent,
  ReadingAssessmentPartContent,
} from "./steps/types";
import {
  asNumber,
  asString,
  normalizeSubmissionProgress,
  type UnknownRecord,
} from "../assessmentNormalization";

const allowedTags = new Set(["b", "i", "em", "strong", "br", "div", "span"]);

const parseOptions: HTMLReactParserOptions = {
  replace: (domNode: DOMNode) => {
    if (domNode.type !== "tag") {
      return;
    }
    if (allowedTags.has(domNode.name)) {
      domNode.attribs = {};
      return;
    }
    return createElement(
      Fragment,
      null,
      domToReact(domNode.children as DOMNode[], parseOptions),
    );
  },
};

export const parseInstructionHtml = (rawHtml: string): ReactNode => {
  if (!rawHtml) {
    return null;
  }
  return parse(rawHtml.replace(/\r\n?/g, "\n"), parseOptions);
};

const normalizeQuestion = (raw: unknown): AssessmentQuestion | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const source = raw as UnknownRecord;
  const questionId = asString(source.question_id || source.questionId);
  const questionType = asString(source.question_type || source.questionType);
  if (!questionId || !questionType) {
    return null;
  }

  const rawOptions = Array.isArray(source.options) ? source.options : [];

  return {
    question_id: questionId,
    placeholder_id: asString(source.placeholder_id || source.placeholderId),
    question_type: questionType,
    part_order: asNumber(source.part_order || source.partOrder),
    question_order: asNumber(source.question_order || source.questionOrder),
    options: rawOptions.filter(
      (option): option is string => typeof option === "string",
    ),
    highlightedText: asString(
      source.highlightedText || source.highlighted_text,
    ),
  };
};

const normalizePart = (
  raw: unknown,
): ReadingAssessmentPartContent | undefined => {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }

  const source = raw as UnknownRecord;
  const rawQuestions = Array.isArray(source.questions) ? source.questions : [];

  return {
    testType: asString(source.testType || source.test_type),
    submissionId: asString(source.submissionId || source.submission_id),
    partOrder: asNumber(source.partOrder || source.part_order),
    prompt: asString(source.prompt || source.text_passage),
    promptImage: asString(source.promptImage || source.prompt_image),
    mazeMaskedPrompt: asString(
      source.mazeMaskedPrompt || source.maze_masked_prompt,
    ),
    vocabMaskedPrompt: asString(
      source.vocabMaskedPrompt || source.vocab_masked_prompt,
    ),
    questions: rawQuestions
      .map((question) => normalizeQuestion(question))
      .filter((question): question is AssessmentQuestion => Boolean(question)),
  };
};

export const normalizeReadingAssessmentContent = (
  raw: unknown,
): ReadingAssessmentContent => {
  const source = raw as UnknownRecord;

  return {
    testType: asString(source.testType || source.test_type),
    overallInstructions: asString(
      source.overallInstructions || source.instructions,
    ),
    instructionsAudio: asString(
      source.instructionsAudio || source.instructions_audio,
    ),
    oralReadingInstructionAudio: asString(
      source.oralReadingInstructionAudio ||
        source.oral_reading_instruction_audio,
    ),
    mazeInstructions: asString(
      source.mazeInstructions || source.maze_instructions,
    ),
    mazeInstructionsAudio: asString(
      source.mazeInstructionsAudio || source.maze_instructions_audio,
    ),
    vocabQuestionInstructionAudio: asString(
      source.vocabQuestionInstructionAudio ||
        source.vocab_question_instruction_audio,
    ),
    recapInstructions: asString(
      source.recapInstructions || source.recap_instructions,
    ),
    recapInstructionsAudio: asString(
      source.recapInstructionsAudio || source.recap_instructions_audio,
    ),
    instructionImage: asString(
      source.instructionImage || source.instruction_image,
    ),
    recapImage: asString(source.recapImage || source.recap_image),
    part: normalizePart(source.part),
    submissionProgress: normalizeSubmissionProgress(source.submissionProgress),
  };
};

export type PromptSegment =
  | { type: "text"; value: string }
  | {
      type: "placeholder";
      raw: string;
      questionId: string;
      tokenIndex: number;
    };

const placeholderPattern = /\{\{\s*(.+?)\s*}}/g;

export const buildPromptSegments = (maskedText: string): PromptSegment[] => {
  const segments: PromptSegment[] = [];
  const tokenSpans: Array<{ start: number; end: number; index: number }> = [];
  const wordPattern = /\S*\{\{\s*.+?\s*}}\S*|\S+/g;

  for (
    let wordMatch = wordPattern.exec(maskedText);
    wordMatch;
    wordMatch = wordPattern.exec(maskedText)
  ) {
    tokenSpans.push({
      start: wordMatch.index,
      end: wordMatch.index + wordMatch[0].length,
      index: tokenSpans.length,
    });
  }

  let cursor = 0;
  for (
    let placeholderMatch = placeholderPattern.exec(maskedText);
    placeholderMatch;
    placeholderMatch = placeholderPattern.exec(maskedText)
  ) {
    const start = placeholderMatch.index;
    const end = placeholderMatch.index + placeholderMatch[0].length;
    if (start > cursor) {
      segments.push({ type: "text", value: maskedText.slice(cursor, start) });
    }

    const tokenIndex =
      tokenSpans.find((span) => start >= span.start && start < span.end)
        ?.index ?? -1;
    segments.push({
      type: "placeholder",
      raw: placeholderMatch[0],
      questionId: (placeholderMatch[1] ?? "").trim(),
      tokenIndex,
    });
    cursor = end;
  }

  if (cursor < maskedText.length) {
    segments.push({ type: "text", value: maskedText.slice(cursor) });
  }

  if (segments.length === 0) {
    return [{ type: "text", value: maskedText }];
  }
  return segments;
};
